/**
 * Created by Frithjof Matthias Schaefer on 13.09.2017.
 */
import {TextField} from "./TextField";
import {TextFieldType} from "./TextFieldType";
import {TEXT_FIELDS_LOGGER} from "../../../../../index";
import {getLogger} from "@goodgamestudios/logger";
import {getUpperLeftCorner} from "../utils/getUpperLeftCorner";

const logger = getLogger(TEXT_FIELDS_LOGGER + ".InputTextFieldService");

/**
 * Simple registry for all input TextFields that are currently present in the game.
 * So far
 * - It can handle the tabbing state / implement tabbing support
 *
 * If possible, the InputTextFieldService.singleton() method should be replaced by dependency injection or any other
 * better implementation of this pattern.
 *
 */
export class InputTextFieldService {

    private static INSTANCE: InputTextFieldService;

    private inputFields: Set<TextField> = new Set();

    /*Private access: singleton pattern implementation*/
    private constructor(){
        window.addEventListener(`keydown`, (e) => this.handleWindowKeyDown(e as KeyboardEvent))
    }

    /**
     * Registeres a TextFields to this Service. Afterwards, all registered input TextFields are accessible
     * for global control of focus / tabbing support.
     * @param {TextField} textField
     */
    public registerInputField(textField: TextField){
        if(textField.type == TextFieldType.INPUT){
            if (!this.inputFields.has(textField)){
                this.inputFields.add(textField);

                // need to use native event handler, otherwise can't cancel the default behavior
                const nativeTextFieldElement = textField.getTextFieldHTMLElement();
                if (nativeTextFieldElement) {
                    nativeTextFieldElement.addEventListener(`keydown`, (e) => this.handleKeyDown(textField, e as KeyboardEvent));
                }
            }
            return;
        }
        logger.warn("The given textfield is not setup correctly to be used as an input field");
    }

    private handleWindowKeyDown(e: KeyboardEvent) {
        let hasFocusedElement:boolean = !!Array.from(this.inputFields.values()).filter((textfield:TextField) => textfield.isFocused)[0];
        if(!hasFocusedElement) {
            this.handleKeyDown(null, e);
        }
    }

    private handleKeyDown(sourceTextField: TextField | null, e: KeyboardEvent) {
        if (e.key === "Tab" || e.code === "Tab") {
            if (sourceTextField) {
                sourceTextField.blur();
            }

            const next = this.nextTextField(sourceTextField, e.shiftKey ? "up" : "down");

            if (next) {
                /**
                 * Since there is not yet a next element on the screen, we need to call `preventDefault` on `Tab` `keydown`, or else the whole window will loose focus (as it would just jump the next control in the browser's chrome)
                 */
                e.preventDefault();
                next.focus();
            }
        }
    }

    private nextTextField(sourceTextField: TextField | null, direction: "up" | "down") {
        let defaultPos: {x: number, y: number}= {x:0,y:0};
        const sourcePosition = sourceTextField ? getUpperLeftCorner(sourceTextField as TextField) : defaultPos;

        const next = Array.from(this.inputFields.values())
            .filter(textField => textField !== sourceTextField && isVisibleOnStage(textField))
            .map(textField => ({ textField: textField, position: getUpperLeftCorner(textField) }))
            .map(({textField, position}) => ({ textField: textField, position:position, inRightDirection: this.isInRightDirection(position, sourcePosition, direction)}))
            .sort(({position: positionA, inRightDirection: inRightDirectionA},{position: positionB, inRightDirection: inRightDirectionB}) => {
                if(inRightDirectionA == inRightDirectionB) {
                    let directionMod:number = direction == "up" ? -1 : 1;
                    if (positionA.y === positionB.y) {
                        return directionMod * (positionA.x - positionB.x);
                    } else {
                        return directionMod * (positionA.y - positionB.y);
                    }
                } else {
                    return inRightDirectionA ? -1 : 1;
                }
            })[0];

        if (next) {
            return next.textField;
        } else {
            return undefined;
        }
    }

    private isInRightDirection(position:{x: number, y: number}, sourcePosition: {x: number, y: number}, direction: "up" | "down"): boolean {
        switch (direction) {
            case "up": {
                if (position.y === sourcePosition.y) {
                    return position.x <= sourcePosition.x;
                } else {
                    return position.y <= sourcePosition.y;
                }
            }
            case "down": {
                if (position.y === sourcePosition.y) {
                    return position.x >= sourcePosition.x;
                } else {
                    return position.y >= sourcePosition.y;
                }
            }
            default:
                return false;
        }
    }

    /**
     * Removes a TextField from the registry of this service.
     * @param {TextField} textField
     */
    public unregisterInputField(textField: TextField){
        this.inputFields.delete(textField);

        logger.warn("The given textfield has not been registered. Skipping removal...");
    }

    /**
     * Retrieve the global InputTextFieldService singleton
     * @returns {InputTextFieldService}
     */
    public static singleton(){
        if(InputTextFieldService.INSTANCE === undefined){
            InputTextFieldService.INSTANCE = new InputTextFieldService();
        }
        return InputTextFieldService.INSTANCE;
    }
}

export function isVisibleOnStage(current: createjs.DisplayObject): boolean {
    let isVisible = current.isVisible() && !!current.stage;
    let iterateDisplayObject: createjs.DisplayObject | null = current;

    while (iterateDisplayObject && isVisible) {
        iterateDisplayObject = iterateDisplayObject.parent;
        if (iterateDisplayObject) {
            isVisible = iterateDisplayObject.isVisible();
        }
    }
    if (isVisible) {
        /**
         * Check whether the is still visually on top of the stage
         * Mouse disabled objects are excluded from the check.
         */

        const bounds = current.getBounds();
        const pos = current.localToGlobal(bounds.x, bounds.y);
        isVisible = current.stage.getObjectUnderPoint(pos.x + 1, pos.y + 1, 1) === current;
    }

    return isVisible;
}
